<template>
  <div class="grid-universe" v-if="isAllClientFlagAvailable">
    <filterBar
      @updateFilters="updateFilters(...arguments)"
      :client="selectedClient"
      :fiduciary="selectedFiduciary"
      :hideFilters="hideFiltersOptions"
      @updateProductFunds="updateProduct"
    />
    <div class="actions-bar">
      <div class="box left">
        <div class="rowCount" v-if="!isLoading">{{ totalRowsNumber }} total funds</div>
        <div class="rowCount" v-else>Loading...</div>
      </div>
      <div class="box right">
        <contextual-menu
          class="import-xls-files-menu"
          position="bottom-left"
          text="Import..."
          :options="formatActionChoices()"
        >
          <mds-button
            variation="icon-only"
            icon="import"
            type="button"
            data-mds-popover="mds-popover"
            aria-haspopup="true"
            aria-expanded="true"
            aria-controls="mds-popover--8809"
            aria-hidden="true"
            @mouseover="onShowTooltip($event, ['Import'], 'bottom-right', 'small')"
            @mouseleave="onHideTooltip()"
            slot="clickTrigger"
          >
          </mds-button>
        </contextual-menu>

        <mds-button
          id="export-view"
          class="exportButton"
          variation="icon-only"
          icon="share"
          type="button"
          :disabled="isExportDisabled"
          @click="openExportModal"
          @mouseover="onShowTooltip($event, ['Export'], 'bottom-right', 'small')"
          @mouseleave="onHideTooltip()"
        >
        </mds-button>
        <mds-button
          id="custom-fund-view"
          class="exportButton"
          variation="icon-only"
          icon="plus"
          type="button"
          :disabled="isExportDisabled"
          @click="navigateToCustomFunds"
          @mouseover="onShowTooltip($event, ['Create Custom Funds'], 'bottom-right', 'small')"
          @mouseleave="onHideTooltip()"
        >
        </mds-button>
      </div>
    </div>
    <div class="grid-wrapper" v-on:scroll.passive="handleScroll">
      <AllClientViewGrid
        v-if="newAllClientViewFlag && isAllClientView"
        :scrolled="scrolled"
        :filters="currentFilters"
        @showToolTip="onShowTooltip"
        @hideToolTip="onHideTooltip"
      />
      <GridUniverseGrid
        v-else
        :scrolled="scrolled"
        :filters="currentFilters"
        @showToolTip="onShowTooltip"
        @hideToolTip="onHideTooltip"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { get } from 'lodash';
import { MdsButton } from '@mds/button';

import { USER_ROLES, FIDUCIARIES } from '@/utils/app-constants';

import ContextualMenu from '@/components/molecules/ContextualMenu';
import FilterBar from '@/components/organisms/FilterBar';
import GridUniverseGrid from '@/components/organisms/GridUniverseGrid';
import AllClientViewGrid from '@/components/organisms/AllClientViewGrid';
import { getNewAllClientViewFlag } from '@/services/UniverseFunds';

export default {
  name: 'grid-universe',
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedFiduciaryCode: (state) => state.user.fiduciary.code,
      selectedClient: (state) => state.grid.client.selected,
      selectedClientName: (state) => state.grid.client.name,
      totalRowsNumber: (state) => state.grid.totalRows,
      selectedTab: (state) => state.grid.tab,
      isLoading: (state) => state.system.isLoading,
      userRole: (state) => state.user.userRole,
      config: (state) => state.system.config,
    }),
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    hideFiltersOptions() {
      return {
        updates: true,
        dateRange: true,
        reviewStatuses: !this.displayReviewStatusFilter,
      };
    },
    localPolicyOptions() {
      // Tabs are applied to 1 Client View
      if (this.currentViewName === 'all-clients') {
        return get(this.policies, `grid.views.${this.currentViewName}`);
      }
      return get(this.policies, `grid.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
    isExportDisabled() {
      return !get(this.localPolicyOptions, 'options.export');
    },
    hasPreferredEnabled() {
      return this.config.grid.columns.preferred;
    },
    displayPreferredFunds() {
      // eslint-disable-next-line max-len
      return this.hasPreferredEnabled && this.userRole === USER_ROLES.CONSULTANT && this.currentViewName === 'one-client';
    },
    isAllClientView() {
      return this.selectedClient === 0 && this.selectedClientName === 'All Clients';
    },
    displayReviewStatusFilter() {
      // eslint-disable-next-line max-len
      return this.userRole === USER_ROLES.CONSULTANT && this.selectedFiduciaryCode === FIDUCIARIES.MONRNINGSTAR && this.currentViewName === 'all-clients';
    },
  },
  components: {
    AllClientViewGrid,
    GridUniverseGrid,
    FilterBar,
    ContextualMenu,
    MdsButton,
  },
  data() {
    return {
      currentFilters: {
        assets: [],
        categories: [],
        status: [],
        reviewStatus: [],
      },
      scrolled: false,
      allStatusMode: false,
      newAllClientViewFlag: false,
      isAllClientFlagAvailable: false,
    };
  },
  created() {
    this.$store.dispatch('setTotalRows', 0);
    getNewAllClientViewFlag().then((result) => {
      if (result?.data?.newAllClientViewFlag) {
        this.newAllClientViewFlag = true;
      }
      this.setNewAllClientViewFlag(this.newAllClientViewFlag);
    }).finally(() => {
      this.isAllClientFlagAvailable = true;
    });
  },
  methods: {
    ...mapActions([
      'setNewAllClientViewFlag',
    ]),
    showToolTipMessage(event, message) {
      this.onShowTooltip(event, message, 'bottom-right', 'small');
    },
    onShowTooltip(event, status, orientation, size) {
      this.$emit('showTooltip', event, status, orientation, size);
    },
    onHideTooltip(event) {
      this.$emit('hideTooltip', event);
    },
    formatActionChoices() {
      const options = [
        {
          id: 'gu-import-lineups',
          disabled: false,
          text: 'Import Lineups',
          link: '/import-lineups',
        },
        {
          id: 'gu-import-approvals',
          disabled: false,
          text: 'Import Approvals',
          link: '/import-approvals',
        },
      ];
      if (this.displayPreferredFunds) {
        const preferredOption = {
          id: 'gu-preferred-fund',
          disabled: false,
          text: 'Import Preferred Funds',
          link: '/import-preferred-funds',
        };
        options.push(preferredOption);
      }
      return options;
    },
    updateFilters(filters, allStatus) {
      this.currentFilters.assets = filters.assets;
      this.currentFilters.categories = filters.categories;
      this.currentFilters.status = filters.status;
      this.currentFilters.reviewStatus = filters.reviewStatus;
      this.allStatusMode = allStatus;
    },
    updateProduct(product, productName, productCode) {
      this.$store.dispatch('setProduct', {
        code: productCode,
        listVisible: false,
        name: productName,
        selected: product,
      });
    },
    openExportModal() {
      this.$store.dispatch('openModal', {
        title: 'Export',
        component: 'export-data',
        data: this.currentFilters,
      });
    },
    handleScroll(event) {
      const footerHeight = 100; // Loading image height + page footer height
      this.scrolled = event.target.offsetHeight + event.target.scrollTop
        >= event.target.scrollHeight - event.target.clientHeight - footerHeight;
    },
    navigateToCustomFunds() {
      this.onHideTooltip();
      this.$router.push({ name: 'createCustomFunds' });
    },
  },
};
</script>

<style lang="scss">
.grid-universe {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  .grid-wrapper {
    height: calc(100% - 61px);
    overflow-y: auto;
    width: 100%;
  }

  @media screen and (max-height: 624px) {
    .single-list, .multiple-list {
      max-height: 250px;
    }
  }
}

.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: $mds-space-2-x;
  font-family: $mds-font-family-sans-serif;
  font-size: $mds-font-size-s;
  margin-top: $mds-space-2-x;

  .rowCount {
    margin: 0 $mds-space-4-x;
    flex-grow: 1;
    font-weight: $mds-font-weight-light;
  }
  .box.right {
    margin-right: $mds-space-inset-4-x;
  }
  button {
    margin-left: $mds-space-inset-2-x;
  }
  .import-xls-files-menu {
    display: inline-block;
  }
  svg.mds-icon.mds-icon__import {
    height: 26px;
    width: 26px;
  }
}
</style>
